import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Alert, Button, IconButton, Snackbar, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import ErrorOutlineOutlined from '@mui/icons-material/ErrorOutlineOutlined';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';
import { OVER_BOARD_Z } from './overBoardTheme';

export const SIDENAV_WIDTH_VAR = '--ft-sidenav-width';

const DURATIONS = { success: 4000, info: 4000, warning: 6000, error: null };
const UNDO_DURATION = 8000;

const ICONS = {
  success: <CheckCircleOutlined fontSize="inherit" />,
  info: <InfoOutlined fontSize="inherit" />,
  warning: <WarningAmberOutlined fontSize="inherit" />,
  error: <ErrorOutlineOutlined fontSize="inherit" />,
};

const NotifyContext = createContext(null);

/**
 * NotifyProvider — app-wide notifications (UI Design Standards §4.9).
 * Bottom-left, one at a time (a new one replaces the current one), always
 * above Bryntum popups. Mount once, inside the app's ThemeProvider.
 */
export const NotifyProvider = ({ children }) => {
  const { ft } = useTheme().palette;
  const [current, setCurrent] = useState(null);
  const [open, setOpen] = useState(false);
  const keyRef = useRef(0);

  const show = useCallback(
    ({ message, severity = 'success', undo, autoHideDuration }) => {
      const tone = DURATIONS[severity] !== undefined ? severity : 'info';
      let duration = undo ? UNDO_DURATION : DURATIONS[tone];
      if (autoHideDuration !== undefined) duration = autoHideDuration;
      keyRef.current += 1;
      setCurrent({
        key: keyRef.current,
        message,
        severity: tone,
        undo,
        duration,
      });
      setOpen(true);
    },
    []
  );

  const notify = useMemo(() => {
    const fn = (options) => show(options);
    Object.keys(DURATIONS).forEach((severity) => {
      fn[severity] = (message, options) =>
        show({ ...options, message, severity });
    });
    return fn;
  }, [show]);

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setOpen(false);
  };

  const handleUndo = async () => {
    const { undo } = current;
    setOpen(false);
    try {
      await undo();
    } catch {
      show({ message: 'Couldn’t undo that change.', severity: 'error' });
    }
  };

  const colors = current ? ft.tones[current.severity] : null;

  return (
    <NotifyContext.Provider value={notify}>
      {children}
      {current && (
        <Snackbar
          key={current.key}
          open={open}
          onClose={handleClose}
          autoHideDuration={current.duration}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          sx={(theme) => ({
            zIndex: OVER_BOARD_Z.snackbar,
            [theme.breakpoints.up('sm')]: {
              left: `calc(var(${SIDENAV_WIDTH_VAR}, 0px) + 24px)`,
            },
          })}
        >
          <Alert
            severity={current.severity}
            variant="standard"
            iconMapping={ICONS}
            action={
              <>
                {current.undo && (
                  <Button
                    size="small"
                    onClick={handleUndo}
                    sx={{
                      color: colors.text,
                      textTransform: 'none',
                      fontWeight: 500,
                    }}
                  >
                    Undo
                  </Button>
                )}
                <Tooltip
                  title="Close"
                  PopperProps={{ sx: { zIndex: OVER_BOARD_Z.snackbar + 1 } }}
                >
                  <IconButton
                    size="small"
                    aria-label="Close"
                    onClick={handleClose}
                    sx={{ color: colors.text }}
                  >
                    <CloseOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            }
            sx={{
              alignItems: 'center',
              minWidth: 288,
              fontSize: 14,
              backgroundColor: colors.bg,
              color: colors.text,
              border: `1px solid ${colors.border}`,
              boxShadow: 3,
              '& .MuiAlert-icon': { color: colors.text },
              '& .MuiAlert-action': { pt: 0, alignItems: 'center' },
            }}
          >
            {current.message}
          </Alert>
        </Snackbar>
      )}
    </NotifyContext.Provider>
  );
};

/**
 * useNotify() — returns notify({ message, severity, undo, autoHideDuration })
 * plus notify.success / .info / .warning / .error(message, options).
 *
 * Timing: success/info 4s, warning 6s, error stays until dismissed, any
 * notification with `undo` 8s. `undo` is the screen's own reversal function;
 * if it rejects, an error notification is shown.
 */
export const useNotify = () => {
  const notify = useContext(NotifyContext);
  if (!notify)
    throw new Error('useNotify must be used inside <NotifyProvider>');
  return notify;
};

export default NotifyProvider;
